import { Currency } from '@/features/dashboard/paymentConfig/types/Currency.ts';
import { MetricDto } from '@/types/stats/MetricDto.ts';
import { MetricUnit } from '@/types/stats/MetricUnit.ts';
import { reportStatsContractBreach } from '@/types/stats/statsContract.ts';
import { formatNumber, formatPrice } from '@/utils/formatters.ts';

/**
 * Metryki są wspólnym kształtem całych statystyk, a nie samych finansów —
 * te same cztery funkcje formatują saldo, budżet i przychód z opcji. Klucz
 * jedzie jako `string`, bo każdy widget ma własne wyliczenie.
 */
export const formatMetricValue = (metric: MetricDto): string => {
  switch (metric.unit) {
    case MetricUnit.MONEY:
      if (metric.currency === null) {
        reportStatsContractBreach(
          `Metryka "${metric.key}" jest kwotą bez waluty.`,
        );
        return formatNumber(metric.value);
      }
      return formatPrice(metric.value, metric.currency);
    case MetricUnit.COUNT:
      return formatNumber(metric.value);
    case MetricUnit.PERCENT:
      return `${Math.round(metric.value)}%`;
    default:
      reportStatsContractBreach(
        `Metryka "${metric.key}" ma nieznaną jednostkę: "${metric.unit}". Dopisz ją do MetricUnit.`,
      );
      return formatNumber(metric.value);
  }
};

export const findMetric = (
  metrics: MetricDto[],
  key: string,
  currency?: Currency,
): MetricDto | undefined =>
  metrics.find(
    (metric) =>
      metric.key === key &&
      (currency === undefined || metric.currency === currency),
  );

/**
 * Waluty, w których w ogóle są kwoty — kart „Wartość rezerwacji” jest tyle,
 * ile walut. Kolejność bierze się z wyliczenia, żeby złotówka stała pierwsza
 * niezależnie od kolejności metryk w odpowiedzi.
 */
export const moneyMetricCurrencies = (metrics: MetricDto[]): Currency[] =>
  Object.values(Currency).filter((currency) =>
    metrics.some(
      (metric) =>
        metric.unit === MetricUnit.MONEY && metric.currency === currency,
    ),
  );

/**
 * Backend porządkuje listy walutowe po swoim wyliczeniu (EUR, PLN, USD), front
 * po swoim (PLN, EUR, USD) — bez tego karty sald i budżetów stałyby na jednym
 * ekranie w dwóch różnych kolejnościach.
 */
export const sortByCurrency = <T extends { currency: Currency }>(
  items: T[],
): T[] => {
  const order = Object.values(Currency);

  return [...items].sort(
    (first, second) =>
      order.indexOf(first.currency) - order.indexOf(second.currency),
  );
};
